import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BENEFITS = [
  { text: 'WIN Verified badge on your business profile' },
  { text: 'Exposure to 5,000+ active investors in the WIN network' },
  { text: 'Access to WIN deal flow network and partnerships' },
];

const TRUST_ITEMS = [
  { icon: 'M12 8v4l3 3M12 3a9 9 0 100 18A9 9 0 0012 3z', text: 'Applications reviewed within 5 business days' },
  { icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z', text: 'WIN Verified badge awarded to approved businesses' },
  { icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', text: 'No cost to apply — completely free' },
];

export default function ApplyToBeFeatureSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-8 sm:py-14 pb-6 relative overflow-hidden"
      style={{ background: '#0B1F3B' }}
    >
      {/* Geometric pattern background */}
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.04 }}>
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="geo" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <polygon points="30,5 55,20 55,40 30,55 5,40 5,20" fill="none" stroke="#D4A520" strokeWidth="1"/>
              <circle cx="30" cy="30" r="3" fill="#D4A520"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#geo)"/>
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section badge */}
        <div className={`flex justify-center mb-6 sm:mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5" style={{ background: 'rgba(212,165,32,0.15)', border: '1px solid rgba(212,165,32,0.3)' }}>
            <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#D4A520' }}>Get Featured</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Left: Copy */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h2
              className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight"
              style={{ color: '#D4A520', fontFamily: 'Cormorant Garamond, serif' }}
            >
              Is Your Business Ready for WIN Investors?
            </h2>
            <p className="text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Apply to have your women-led business featured in front of 5,000+ active investors in the WIN network.
              Our team reviews every application personally.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              {BENEFITS?.map((b, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${200 + i * 100}ms` }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: '#D4A520' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l2.5 2.5L10 3" stroke="#0B1F3B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className="text-base" style={{ color: 'rgba(255,255,255,0.85)' }}>{b?.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Application Card */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div
              className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full"
              style={{ borderTop: '4px solid #D4A520' }}
            >
              <div className="p-5 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,165,32,0.1)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg" style={{ color: '#0B1F3B', fontFamily: 'Cormorant Garamond, serif' }}>Start Your Application</h3>
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>Takes about 10 minutes to complete</p>
                  </div>
                </div>

                <p className="text-sm mb-6 leading-relaxed" style={{ color: '#6B7280' }}>
                  Join hundreds of women entrepreneurs who have gained access to WIN's exclusive investor network.
                  Our 4-step application process is designed to showcase your business in the best light.
                </p>

                <div className="space-y-2 mb-6">
                  {['Founder & contact information', 'Business details & sector', 'Investment ask & financials', 'Review & submit']?.map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: '#0B1F3B', color: '#D4A520' }}>{i + 1}</div>
                      <span className="text-sm" style={{ color: '#374151' }}>{step}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => navigate('/business-application-modal')}
                  className="w-full py-4 rounded-xl font-bold text-base transition-all duration-200 hover:opacity-90 active:scale-[0.98] relative overflow-hidden"
                  style={{ background: '#D4A520', color: '#0B1F3B', boxShadow: '0 4px 20px rgba(212,165,32,0.3)' }}
                >
                  Apply Now →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className={`mt-8 sm:mt-10 pt-6 sm:pt-8 border-t transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {TRUST_ITEMS?.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(212,165,32,0.15)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d={item?.icon} stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{item?.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
